/**
 * Order priority — three levels, read directly (TASK-4833).
 *
 * `Order.priority` and `OrderTemplate.priority` ARE the level now: the backend
 * stores `"low" | "medium" | "high"` and the numeric 1–99 column was removed from
 * the database entirely (migration `orders.0033_priority_three_levels`). So this
 * module no longer thresholds a number — it validates and passes through.
 *
 * `normalizePriority` exists for one reason: a row loaded from a stale cache, a
 * bookmarked URL or an older mobile payload can still carry a number. Rather than
 * render an empty badge for it, we apply the SAME thresholds the migration used
 * (`>= 80` high, `>= 50` medium, else low) so the fallback agrees with what was
 * written to the database. Anything else falls back to `medium`, matching the
 * column default.
 */

export type PriorityLevel = "high" | "medium" | "low";

/** The stored values, in ascending importance — the order the UI presents them in. */
export const PRIORITY_LEVELS: readonly PriorityLevel[] = [
  "low",
  "medium",
  "high",
] as const;

export const DEFAULT_PRIORITY: PriorityLevel = "medium";

/** Legacy numeric thresholds — kept ONLY for the stale-payload fallback below. */
const LEGACY_HIGH_MIN = 80;
const LEGACY_MEDIUM_MIN = 50;

export function isPriorityLevel(value: unknown): value is PriorityLevel {
  return (
    typeof value === "string" &&
    (PRIORITY_LEVELS as readonly string[]).includes(value)
  );
}

/**
 * Coerce whatever the API handed us into a level.
 *
 * The happy path is the first branch — the value already IS the level.
 */
export function normalizePriority(
  value: PriorityLevel | number | string | null | undefined,
): PriorityLevel {
  if (isPriorityLevel(value)) return value;
  // Guard the numeric branch explicitly. `Number(null)` and `Number("")` are BOTH
  // `0` — finite — so a null/blank priority would otherwise be read as the number
  // zero and render as «Низкий», silently downgrading an order that simply had no
  // value. Only a real number, or a string that is entirely a number, may enter.
  const asNumber =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : Number.NaN;
  if (Number.isFinite(asNumber)) {
    if (asNumber >= LEGACY_HIGH_MIN) return "high";
    if (asNumber >= LEGACY_MEDIUM_MIN) return "medium";
    return "low";
  }
  return DEFAULT_PRIORITY;
}

export function priorityBadge(
  priority: PriorityLevel | number | string | null | undefined,
): {
  level: PriorityLevel;
  bg: string;
  text: string;
  dot: string;
} {
  const level = normalizePriority(priority);
  if (level === "high") {
    return {
      level,
      bg: "bg-red-50",
      text: "text-red-700",
      dot: "bg-red-400",
    };
  }
  if (level === "medium") {
    return {
      level,
      bg: "bg-amber-50",
      text: "text-amber-700",
      dot: "bg-amber-400",
    };
  }
  return {
    level,
    bg: "bg-gray-100",
    text: "text-gray-500",
    dot: "bg-gray-400",
  };
}
